import { FilterMethod, ITableDatasource } from '@app/core/data-table/table-datasource';
import { ISearchRequest } from '@app/core/model/search/search-request';
import { ISearchResultItem } from '@app/core/model/search/search-result-item';
import { IListResponseWrapper } from '@app/core/model/v2-api/v2-api-wrappers';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { ISearchFilter } from '@app/core/services/search/resource-search/search-query-types.service';
import { SearchQueryService } from '@app/core/services/search/resource-search/search-query.service';
import { Constants } from '@app/core/utilities/constants';
import { StringUtilities } from '@app/core/utilities/string-utilities';
import { IServerSideRequestOptions } from '@bssh/comp-lib';
import environment from '@environments/environment';
import { GridOptions, IServerSideGetRowsRequest } from 'ag-grid-community';
import _, { cloneDeep } from 'lodash';

export interface ISearchTableDataSource extends ITableDatasource<ISearchRequest, IListResponseWrapper<ISearchResultItem>> {
  datasourceOptions: ISearchDataSourceOptions;
  rawTextFilterProcessing: (filterValue: string, gridOptions: GridOptions) => void;
  rawTextFilterColumnName: string;
}
export class SearchDataSource implements ISearchTableDataSource {

  public response: IListResponseWrapper<ISearchResultItem> = null;

  constructor(
    public searchQueryService: SearchQueryService,
    public resourceConfig: ISearchResourceConfig,
    public searchDataSourceOptions?: ISearchDataSourceOptions
  ) {
    this.setDefaultParams();
  }

  get requestOptions(): IServerSideRequestOptions {
    return {
      origin: this.origin,
      path: this.path,
      params: this.defaultParams,
      preRequestParamsProcessing: (params, rowsRequest) => this.getUpdatedParams(params, rowsRequest),
      postSuccessRequestCallback: (params, rowsRequest, response) => this.getRowsRequest(params, rowsRequest, response),
      postErrorRequestCallback: (params, rowsRequest, err) => {
        console.error(err);
      }
    };
  }

  get datasourceOptions(): ISearchDataSourceOptions {
    return {
      useStrictPaging: this.useStrictPaging,
      defaultSortby: this.sortBy,
      defaultSortDir: this.sortDir,
      defaultLimit: this.limit,
      scope: this.scope,
      pagination: this.pagination
    };
  }

  get rawTextFilterColumnName(): string {
    return null;
  }

  origin = StringUtilities.trimTrailingSlash(environment.apiEndpoint);
  path = 'search';
  defaultParams: ISearchRequest;

  public scope = '';
  public offset = 0;
  public pagination = true;

  // Defaults:
  public limit = 100;
  public sortBy = Constants.ServerSortFields.ModifiedOn;
  public sortDir = Constants.ServerSortDirection.Desc;
  private useStrictPaging = false;
  private fileExtension: IFileExtensionFilter = null;
  private datasetTypeSlug: string = null;

  private _rawTextQuery: string;

  /* 
    Base search filters are applied to the datasource before the user interacts with the data. The user doesn't see these filters
    and we apply these behind the scenes because we understand user expectations. A good example of this would be the sample base query
    where the baseQuery always includes 'Status:Complete'. Users don't expect to be able to interact/select samples that are still being
    generated.
  */
  public baseSearchFilters: ISearchFilter[] = [];

  // All search filters. _searchFilters = base filters + user applied column filters
  private _searchFilters: ISearchFilter[] = [];

  
  private _prevQueryString: string = null;

  filterMethods: {[field: string]: FilterMethod<any>} = {};

  rawTextFilterProcessing = (filterValue: string, gridOptions: GridOptions) => {
    this._rawTextQuery = filterValue;
    gridOptions.api.onFilterChanged();
  }

  applyPaginationParams(params: ISearchRequest, startRow: number) {
    const updatedParams = cloneDeep(params);
    updatedParams.offset = startRow;
    if (this.useStrictPaging && updatedParams.sortBy === Constants.ServerSortFields.Score) {
      updatedParams.pagingStyle = Constants.PagingStyles.Strict;
    }
    return updatedParams;
  }

  applySortParams(params: ISearchRequest, sortModel: { colId: string, sort: string }[]) {
    const updatedParams = cloneDeep(params);
    const sortRequest = sortModel[0];
    updatedParams.sortBy = sortRequest.colId;
    updatedParams.sortDir = sortRequest.sort;
    return updatedParams;
  }

  applyFilterParams(params: ISearchRequest, filterModel: any): any {
    for(let [field, filterObj] of Object.entries(filterModel)){
      const searchFilter = this.filterMethods[field](field as any, filterObj) as any;

      if(Array.isArray(searchFilter)) {
        this._searchFilters.push(...searchFilter);
      } else {
        this._searchFilters.push(searchFilter);
      }

      
    }
  }

  getUpdatedParams(params: ISearchRequest, rowsRequest: IServerSideGetRowsRequest) {
    const { startRow, sortModel, filterModel } = rowsRequest;
    this._searchFilters = [...this.baseSearchFilters];

    let updatedParams = cloneDeep(params);

    if (!_.isEmpty(filterModel)) {
      this.applyFilterParams(updatedParams, filterModel);
    } else {
      this._rawTextQuery =  this._rawTextQuery ? this._rawTextQuery : '';
    }

    
    // sorting params
    if (!_.isEmpty(rowsRequest.sortModel)) {
      updatedParams = this.applySortParams(updatedParams, sortModel);
    }
    // Search query string params
    updatedParams = this.setQueryString(updatedParams);
    // Sort by score when it is a raw text search
    if (!_.isEmpty(this._rawTextQuery)) {
      updatedParams.sortBy = Constants.ServerSortFields.Score;
    } else if (_.isEmpty(updatedParams.sortBy)) {
      updatedParams.sortBy = this.sortBy;
    }
    if (_.isEmpty(updatedParams.sortDir)) {
      updatedParams.sortDir = this.sortDir;
    }

    // Paging params
    updatedParams = this.applyPaginationParams(updatedParams, startRow);

    // Scope i.e. projects, biosamples etc.
    updatedParams.scope = this.resourceConfig.searchScope;
    return updatedParams;
  }

  getRowsRequest(params: ISearchRequest, rowsRequest: IServerSideGetRowsRequest, response: IListResponseWrapper<ISearchResultItem>) {
    this.response = response;
    return {
      ...rowsRequest,
      rowData: response.Items,
      startRow: response.Paging.Offset,
      lastRow: this.pagination ? response.Paging.TotalCount : response.Paging.Offset + response.Items.length
      /*
      Why this 'lastRow' hack is necessary

      When pagination is turned on:
      lastRow parameter is used to determine if there should be a next page arrow enabled so the user
      has the ability to load the next set of rows (this makes sense)

      When pagination is turned off:
      grid will attempt to display all rows at once. What this means is that it will use the 'lastRow' value to determine
      how many rows it should render, even if it doesn't have data beyond what's found in the 'rowData' value
      So you'll end up with a bunch of empty rows render
      */
    };
  }

  setDefaultRawTextQuery(query: string): void {
    this._rawTextQuery = query;
  }


  setQueryString(params: ISearchRequest): ISearchRequest {
    const updatedParams = cloneDeep(params);

    /*
      Different types of applied query params
      These must all be COMBINED when generating the search query applied for the resource chooser

      Scope: EVERYWHERE
      1. "baseQuery" param value from the src/app/core/services/resource-dictionary/search-resource-dictionary.service.ts service. Example is Status:Complete for samples

      Scope: APP LAUNCH ONLY
      2. Params passed in from the resource chooser component specifically for that resource. Will apply for launching of all apps. Example biosample chooser with HasQcPassedFastqDatasets:true
      We only want this on app launch and not file uploader. In app launch user will only want to select biosamples that have FASTQ data. For file uploader user can create
      biosamples and then assign the created fastq uploaded dataset to be linked to the newly created biosample

      Scope: APP LAUNCH ONLY
      3. "searchQuery" property. This is created inside the app launch form created by the developer in the dev portal. This is a requirement known by the developer
      and cannot be altered by the application consumer.

      Scope: EVERYWHERE
      4. User chooser params - This consitutes anything the user applies while inside the chooser (eg column filter, column sort) 
      
      */

    const queryString = this.searchQueryService.buildQueryString( 
      this.searchDataSourceOptions.extendedBaseQuery,
      this._rawTextQuery,
      this._searchFilters,
      this.resourceConfig
    );
     
    if (this._prevQueryString != null && queryString !== this._prevQueryString) {
      // reset the offset if the query has changed
      updatedParams.offset = 0;
    }
    this._prevQueryString = queryString;
    updatedParams.query = queryString;
    return updatedParams;
  }

  private setDefaultParams() {
    this.limit = _.get(this.searchDataSourceOptions, 'defaultLimit', this.limit);
    this.sortBy = _.get(this.searchDataSourceOptions, 'defaultSortby', this.sortBy);
    this.sortDir = _.get(this.searchDataSourceOptions, 'defaultSortDir', this.sortDir);
    this.useStrictPaging = _.get(this.searchDataSourceOptions, 'useStrictPaging', this.useStrictPaging);
    this.scope = _.get(this.searchDataSourceOptions, 'scope', this.resourceConfig.searchScope);
    this.pagination = _.get(this.searchDataSourceOptions, 'pagination', this.pagination);
    this.fileExtension = _.get(this.searchDataSourceOptions, 'fileExtension', this.fileExtension);
    this.datasetTypeSlug = _.get(this.searchDataSourceOptions, 'datasetTypeSlug', this.datasetTypeSlug);

    const defaultRawTextQuery = this.searchDataSourceOptions.defaultRawTextQuery;
    if(defaultRawTextQuery) {
      this.setDefaultRawTextQuery(defaultRawTextQuery);
    }

    this.defaultParams = {
      offset: this.offset,
      limit: this.limit,
      sortDir: this.sortDir,
      sortBy: this.sortBy,
      query: '',
      scope: this.scope,
      pagination: this.pagination
    };

    if (this.fileExtension && this.fileExtension.value) {
      if (this.fileExtension.value[0] !== '.') {
        this.fileExtension.value = `.${this.fileExtension.value}`;
      }
      this.baseSearchFilters.push({
        facets: ['fileextension'],
        currentChoice: {
          value: this.fileExtension.value,
          label: this.fileExtension.value,
          strict: this.fileExtension.strict
        }
      })
    }

    if(this.datasetTypeSlug) {
      this.baseSearchFilters.push({
        facets: ['datasettype.slug'],
        currentChoice: {
          value: this.datasetTypeSlug,
          label: this.datasetTypeSlug
        }
      })
    }
  }
}


export interface ISearchDataSourceOptions {
  useStrictPaging?: boolean;
  defaultSortby?: string;
  defaultSortDir?: string;
  defaultLimit?: number;
  defaultRawTextQuery?: string;
  scope?: string;
  pagination?: boolean;
  fileExtension?: IFileExtensionFilter;
  datasetTypeSlug?: string;
  extendedBaseQuery?: string;
}

export interface IFileExtensionFilter {
  value: string;
  strict?: boolean;
}
